import type { SemanticColorToken } from '../../../themes/types';
import type { StyleResolverContext, CssFragment } from './index';
import { DEFAULT_FONT_COLOR } from '../../../constants/text';
import type { BoxBorderProps, DirectionalBorderValue } from './border';
import { resolveBorderColor, resolveBorders, resolveBorderCss } from './border';

// ============================================================================
// Types
// ============================================================================

export interface ColorProps {
  color?: SemanticColorToken;
  background?: SemanticColorToken;
}

interface StripeValues {
  width: number;
  gap: number;
  angle: number;
}

/** Props for box background, hover, stripes, and borders. Border props are inherited from BoxBorderProps. */
export interface BackgroundColorsProps extends BoxBorderProps {
  bg?: SemanticColorToken;
  bgHover?: SemanticColorToken;
  bgStripeColor?: SemanticColorToken;
  stripesWidth?: number;
  stripesGap?: number;
  stripesAngle?: number;
  borderWidth?: number;
}

// ============================================================================
// Text color resolution
// ============================================================================

const pickColorProps = <T extends Partial<ColorProps>>(props: T): ColorProps => {
  const { color, background } = props;
  return { color, background };
};

const resolveColorsCss = (props: ColorProps, getColor: (token: SemanticColorToken) => string): CssFragment[] => {
  const { color, background } = props;
  return [
    color ? `color: ${getColor(color)};` : null,
    background ? `background-color: ${getColor(background)};` : null,
  ];
};

// ============================================================================
// Background color helpers
// ============================================================================

const resolveBackgroundColors = (
  bg: SemanticColorToken | undefined,
  bgHover: SemanticColorToken | undefined,
  getColor: (token: SemanticColorToken) => string,
): { background: string | undefined; backgroundHover: string | undefined } => ({
  background: bg ? getColor(bg) : undefined,
  backgroundHover: bgHover ? getColor(bgHover) : undefined,
});

/** Returns CSS fragment for solid box background. */
const resolveBackgroundCss = (background: string | undefined): CssFragment =>
  background ? `background-color: ${background};` : null;

/** Returns CSS fragment for box background hover state. */
const resolveHoverCss = (backgroundHover: string | undefined): CssFragment =>
  backgroundHover ? `&:hover {\n  background-color: ${backgroundHover};\n}` : null;

// ============================================================================
// Stripes
// ============================================================================

const getStripeValues = (
  getLayout: StyleResolverContext['getLayout'],
  stripesWidth?: number,
  stripesGap?: number,
  stripesAngle?: number,
): StripeValues => {
  const defaultWidth = parseFloat(String(getLayout('stripes.default', 'width')));
  const defaultGap = parseFloat(String(getLayout('stripes.default', 'gap')));
  const defaultAngle = parseFloat(String(getLayout('stripes.default', 'angle')));

  return {
    width: stripesWidth ?? defaultWidth,
    gap: stripesGap ?? (stripesWidth !== undefined ? stripesWidth : defaultGap),
    angle: stripesAngle ?? defaultAngle,
  };
};

const resolveStripeCss = (background: string, stripeColor: string, sv: StripeValues): CssFragment =>
  `background: repeating-linear-gradient(${sv.angle}deg, ${stripeColor}, ${stripeColor} ${sv.width}px, ${background} ${sv.width}px, ${background} ${sv.width + sv.gap}px);`;

// ============================================================================
// Background colors composite resolver
// ============================================================================

const pickBackgroundColorsProps = <T extends Partial<BackgroundColorsProps>>(props: T): BackgroundColorsProps => {
  const {
    bg,
    bgHover,
    bgStripeColor,
    borderColor,
    border,
    borderTop,
    borderBottom,
    borderLeft,
    borderRight,
    stripesWidth,
    stripesGap,
    stripesAngle,
    borderWidth,
  } = props;
  return {
    bg,
    bgHover,
    bgStripeColor,
    borderColor,
    border,
    borderTop,
    borderBottom,
    borderLeft,
    borderRight,
    stripesWidth,
    stripesGap,
    stripesAngle,
    borderWidth,
  };
};

/** Returns CSS fragments for box background, hover, stripes, and borders. */
const resolveBackgroundColorsCss = (
  props: BackgroundColorsProps,
  ctx: Pick<StyleResolverContext, 'getColor' | 'getLayout' | 'getBorderWidth'>,
): CssFragment[] => {
  const { getColor, getLayout, getBorderWidth } = ctx;
  const resolveDirectionalBorder = (value: DirectionalBorderValue) => resolveBorderColor(value, getColor);
  const {
    bg,
    bgHover,
    bgStripeColor,
    borderColor,
    border,
    borderTop,
    borderBottom,
    borderLeft,
    borderRight,
    stripesWidth,
    stripesGap,
    stripesAngle,
    borderWidth,
  } = props;

  const { background, backgroundHover } = resolveBackgroundColors(bg, bgHover, getColor);
  const stripeValues = getStripeValues(getLayout, stripesWidth, stripesGap, stripesAngle);
  const defaultBorderWidth = getBorderWidth('default');

  const borderProps = { borderColor, border, borderTop, borderBottom, borderLeft, borderRight };
  const borders = resolveBorders(borderProps, getColor, resolveDirectionalBorder);

  const backgroundFragment =
    bgStripeColor && background
      ? resolveStripeCss(background, getColor(bgStripeColor), stripeValues)
      : resolveBackgroundCss(background);

  return [
    backgroundFragment,
    resolveHoverCss(backgroundHover),
    ...resolveBorderCss(borders, defaultBorderWidth, borderWidth),
  ];
};

// ============================================================================
// Style resolvers (entry points for STYLE_RESOLVERS registry)
// ============================================================================

export const resolveColorsStyleCss = (props: Record<string, unknown>, theme: StyleResolverContext): CssFragment[] => {
  const { color = DEFAULT_FONT_COLOR, background } = pickColorProps(props as Partial<ColorProps>);
  return resolveColorsCss({ color, background }, theme.getColor);
};

export const resolveBackgroundColorsStyleCss = (
  props: Record<string, unknown>,
  theme: StyleResolverContext,
): CssFragment[] =>
  resolveBackgroundColorsCss(pickBackgroundColorsProps(props as Partial<BackgroundColorsProps>), theme);
